import type { components } from "./schema";

/**
 * The only file in the bundle that names a generated component schema. Every screen imports its types from here,
 * so a rename in the contract is one edit and a type error rather than a silent `any`. See this task file's
 * "Interface assumptions" table for what is assumed and why.
 */
type S = components["schemas"];

export type Session = S["SessionOut"];
export type Systems = S["SystemsOut"];
/** Phase 5 (P1a §11): anonymous feature discovery, read once on boot by `FeaturesProvider`. */
export type Features = S["FeaturesOut"];
/** `POST /api/v1/auth/login`'s body: `{email, expires_at, csrf_token}` — the same shape `SessionOut` carries,
 * under the field name the multiuser route actually returns (`email`, not `operator`). */
export type LoginOut = S["LoginOut"];
/** xmg-sec-mfa (S3a): two-step authentication. `MfaSessionState` is where a login-issued session stands on its
 * second factor; `null` on `Session.mfa` means an operator-key session, which has no user and no factor. */
export type MfaSessionState = LoginOut["mfa"];
export type MfaStatus = S["MfaStatusOut"];
export type MfaEnrollment = S["MfaEnrollmentOut"];
/** xmg-sec-mfa-takeover: where the enrolment set-up code was mailed (masked) and until when it works. */
export type MfaEnrollmentCodeSent = S["MfaEnrollmentCodeSentOut"];
export type MfaRecoveryCodes = S["MfaRecoveryCodesOut"];
export type MfaVerified = S["MfaVerifiedOut"];
export type MfaVerifyBody = S["MfaVerifyIn"];
// The contract names below are the ones `openapi-typescript` emits from the COMMITTED document; where two
// routers declare a model of the same name FastAPI qualifies it with the module path.
export type SystemCapabilities = S["lucimigration__api__routers__meta__CapabilitiesOut"];
export type Connection = S["ConnectionOut"];
export type XeroAuthorization = S["XeroAuthorizeOut"];
export type XeroHandoff = S["XeroHandoffOut"];
export type Migration = S["MigrationOut"];
export type MigrationEvent = S["MigrationEventOut"];
export type Scope = S["ScopeOut"];
export type MigrationProfile = S["ProfileOut"];
export type PreflightItem = S["PreflightIssueOut"];
export type OrgProfile = S["OrgProfileOut"];
export type CapabilityMatrix = S["lucimigration__api__schemas__migrations__CapabilitiesOut"];
/** The canonical `Entity` enum, as the contract spells it. */
export type Entity = S["Entity"];
export type Consent = S["ConsentOut"];
/** The request body of `create_migration`, so the create hook is not a `Record<string, unknown>` cast. */
export type CreateMigrationBody = S["CreateMigrationIn"];
/**
 * The request body of `signoff_migration`, taken from the CONTRACT rather than retyped by hand. `SignoffIn`
 * has `extra="forbid"`, so a hand-written `{signer?: string; ...}` compiled cleanly and made every conversion
 * sign-off a flat `422` at runtime. Deriving it here means the server is the only definition of the shape and
 * an extra key is a TYPE error, caught by `npm run typecheck`, before it can reach a browser.
 */
export type SignoffBody = S["SignoffIn"];
export type AuditEvent = S["AuditEventOut"];
export type Job = S["JobOut"];
export type Accepted = S["JobAcceptedOut"];
export type Scorecard = S["ScorecardOut"];
export type ControlTotal = S["ControlTotalOut"];
export type ReviewItem = S["ReviewItemOut"];
/** xmg-coafast: the bulk "accept all suggestions" result. */
export type AcceptSuggestionsResult = S["AcceptSuggestionsOut"];
/** FX opening-balance spec §3.7: the foreign-currency bank accounts screen. */
export type FxOpeningBalances = S["FxOpeningBalancesOut"];
export type FxAccount = S["FxAccountOut"];
/** MT-17 design §5: one row of `ScopeOut.source_bank_accounts`. */
export type SourceBankAccount = S["SourceBankAccountOut"];
export type SourceUnreconciledDerived = S["SourceUnreconciledDerivedOut"];
/** xmg-xerobudget: `ScopeOut.api_budget`, the scope-time source-API call estimate against the daily budget. */
export type ApiBudget = S["ApiBudgetOut"];
export type FxFinding = S["FxFindingOut"];
export type AccountMapping = S["CoaMappingOut"];
export type TaxMapping = S["TaxMappingOut"];
export type ContactMerge = S["ContactMergeOut"];
export type EvidencePack = S["EvidencePackOut"];
export type ConnectorSwitch = S["ConnectorSwitchOut"];
export type VendorAlert = S["VendorAlertOut"];
export type VendorLimit = S["VendorLimitOut"];
/** hmrc-rerun-action design §5.4/§3.6: `GET .../hmrc-verification`'s body. */
export type HmrcVerification = S["HmrcVerificationOut"];
/** xmg-lmux (1): what the conversion sign-off dialog shows about VAT locking. */
export type SignoffVatLock = S["SignoffVatLockOut"];
/**
 * `workflow/purge.py::PurgeCount`, as §B6.4 renders it. It is **not** a contract component: endpoint 54
 * defers the sweep and answers `JobAcceptedOut`, so a dry run carries no counts today. The type is kept
 * structural, and `usePurge` marks the field optional, so the table renders whenever the API grows one.
 */
export type PurgeCount = { target: string; count: number };
export type PackageManifest = S["PackageManifestOut"];

// ---------------------------------------------------------------- P5-web: practice mode
/** `landing`/`GET /api/v1/practices` row: the tenant-switcher's own summary, not the detail. */
export type PracticeSummary = S["PracticeSummaryOut"];
export type PracticeDetail = S["PracticeDetailOut"];
export type CreatePracticeBody = S["CreatePracticeIn"];
export type PracticeRole = PracticeSummary["role"];
export type Member = S["MemberOut"];
export type ChangeRoleBody = S["ChangeRoleIn"];
export type Invitation = S["InvitationOut"];
export type CreateInvitationBody = S["CreateInvitationIn"];
export type AcceptInvitationBody = S["AcceptInvitationIn"];
export type AcceptInvitationResult = S["AcceptInvitationOut"];
export type PracticeClient = S["PracticeClientOut"];
export type ServiceAuthorisation = S["ServiceAuthorisationOut"];
export type CreateBatchRunBody = S["CreateBatchRunIn"];
/** The synchronous response of `create_batch_run`: which client ids were admitted and which were not. */
export type BatchRunCreated = S["BatchRunOut"];
export type RejectedClient = S["RejectedClient"];
/** `get_batch_run`/`cancel_batch_run`'s shape — counts, status and what the practice should do next. */
export type BatchRunDetail = S["BatchRunDetail"];
/** `list_practice_batch_runs`'s row shape — the practice-scoped history the web bundle used to fake out of
 * `sessionStorage` because the endpoint did not exist yet (P5-backend-2 landed it). */
export type BatchRunSummary = S["BatchRunSummaryOut"];
export type ClientMigrationState = S["ClientMigrationStateOut"];
export type BatchCompleteness = S["BatchCompletenessOut"];
export type ClientCompleteness = S["ClientCompletenessOut"];
export type AuthorityGap = S["AuthorityGapOut"];
export type RateBudget = S["RateBudgetOut"];
export type SystemBudget = S["SystemBudgetOut"];

/** §A4.1. Structural, not generated: FastAPI emits one component per `Page[T]` instantiation. */
export type Page<T> = { items: T[]; next_cursor: string | null; limit: number };

/** `workflow/states.py::MigrationState`, verbatim and in declaration order. */
export const MIGRATION_STATES = [
  "draft",
  "connecting",
  "profiling",
  "scoping",
  "awaiting_scope_signoff",
  "extracting",
  "mapping",
  "awaiting_mapping_review",
  "transforming",
  "dry_running",
  "awaiting_conversion_signoff",
  "loading",
  "verifying",
  "hypercare",
  "locked",
  "failed",
  "cancelled",
  "rolling_back",
  "rolled_back",
] as const;

export type MigrationState = (typeof MIGRATION_STATES)[number];

/** `workflow/runner.py::WAITING_STATES` = SIGNOFF_STATES | TERMINAL | {failed, hypercare}. §A9.8 polls on it. */
export const WAITING_STATES: ReadonlySet<MigrationState> = new Set<MigrationState>([
  "awaiting_scope_signoff",
  "awaiting_mapping_review",
  "awaiting_conversion_signoff",
  "locked",
  "cancelled",
  "rolled_back",
  "failed",
  "hypercare",
]);

/** `workflow/states.py::TERMINAL`. Polling stops here. */
export const TERMINAL_STATES: ReadonlySet<MigrationState> = new Set<MigrationState>([
  "locked",
  "cancelled",
  "rolled_back",
]);

export type RevokeState = "active" | "revoked" | "revoke_failed";
export type JobStatus = "todo" | "doing" | "succeeded" | "failed" | "cancelled" | "aborting" | "aborted";
export type Severity = "BLOCK" | "WARN" | "INFO";
export type Support = "FULL" | "PARTIAL" | "NONE";
// xero-control-legs design Critique C7 / Final decision 9: "INFO" is a legal `ControlTotalOut.status`
// value already (the server has sent it for `vat_returns` `notes:*` rows since phase 3, and this design
// adds it for `asset_register_account_level` `source_ledger:*`/`pair:*`/`per_asset` rows too) -- this
// type only ever lagged the real wire shape, which carries no enum for `status` at all
// (`ControlTotalOut.status: str`). Widened here so `row.status === "INFO"` type-checks; no new i18n key
// (Final decision 9): an INFO row's pill falls back to its own key text, same as any other untranslated
// status would.
export type CheckStatus = "PASS" | "FAIL" | "WARN" | "MISSING" | "INFO";

/** §B7.6: one date helper, ISO, never a locale guess. */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const cut = value.indexOf("T");
  return cut === -1 ? value : value.slice(0, cut);
}

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * An ISO date as the operator reads it ("2026-08-31" -> "31 Aug 2026"). Fixed English month names and no
 * `Intl`/`Date` parsing, so the day never shifts with the browser's timezone or locale. Anything that is not a
 * plain `YYYY-MM-DD` comes back unchanged.
 */
export function formatDateReadable(value: string | null | undefined): string {
  const iso = formatDate(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return iso;
  const month = MONTHS[Number(match[2]) - 1];
  return month === undefined ? iso : `${Number(match[3])} ${month} ${match[1]}`;
}

/** The ISO day before an ISO date ("2026-09-01" -> "2026-08-31"); `""` when the input is not a plain date. */
export function dayBefore(value: string | null | undefined): string {
  const iso = formatDate(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

/** ISO date and minute, for timeline rows. Still not a locale guess. */
/** "Oct 07 14:02" — the timeline rail's short stamp (Status.html). No date is the empty string. */
export function formatDateTimeShort(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = MONTHS[parsed.getMonth()] ?? "";
  const day = String(parsed.getDate()).padStart(2, "0");
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${month} ${day} ${hours}:${minutes}`;
}

/** "Oct 21 17:53" in the operator's local time -- how every paused-until time is written. */
export function formatPauseAt(value: string | null | undefined): string {
  return formatDateTimeShort(value);
}

export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  return value.replace("T", " ").slice(0, 16);
}

/** The wizard's source-derived defaults (`GET /connections/{id}/conversion-basis-suggestion`). */
export type ConversionBasisSuggestion = S["ConversionBasisSuggestionOut"];
/** xmg-orgadopt: the settings an EMPTY LuciBook organisation takes (or would take) from the source. */
export type AdoptSettings = S["AdoptSettingsOut"];
/** Body of `set_preflight_override`: exactly the overrides a failed preflight may record. */
export type PreflightOverrideBody = S["PreflightOverrideIn"];
